/**
 * Account Target Modal
 * Modal for creating/editing account balance targets
 */
import { useState, useEffect, useMemo } from 'react';
import {
  useCreateAccountTarget,
  useUpdateAccountTarget,
  useAccountTargets,
} from '@/hooks/useAllocation';
import { useAccounts } from '@/hooks/useAccounts';
import Button from '@/components/ui/Button';
import {
  TARGET_TYPE_DISPLAY_NAMES,
  type TargetType,
  type CreateAccountTargetRequest,
  type UpdateAccountTargetRequest,
} from '@/types/allocation';

interface AccountTargetModalProps {
  isOpen: boolean;
  accountId: string | null;
  onClose: () => void;
}

export default function AccountTargetModal({
  isOpen,
  accountId,
  onClose,
}: AccountTargetModalProps) {
  const isEditing = accountId !== 'new' && accountId !== null;
  const { data: targetsData } = useAccountTargets();
  const { data: accountsData } = useAccounts();
  const { mutate: createTarget, isPending: isCreating } = useCreateAccountTarget();
  const { mutate: updateTarget, isPending: isUpdating } = useUpdateAccountTarget();

  // Get accounts that don't already have targets (for new target creation)
  const availableAccounts = useMemo(() => {
    const accounts = accountsData ?? [];
    const accountsWithTargets = new Set(targetsData?.targets.map((t) => t.accountId) ?? []);
    return accounts.filter((a) => !accountsWithTargets.has(a.id));
  }, [accountsData, targetsData]);

  // Form state
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [targetType, setTargetType] = useState<TargetType>('exact_amount');
  const [targetValue, setTargetValue] = useState<string>('');
  const [targetValueMax, setTargetValueMax] = useState<string>('');

  // Find existing target if editing (now nested under .target)
  const existingAccountWithTarget = targetsData?.targets.find((t) => t.accountId === accountId);
  const existingTarget = existingAccountWithTarget?.target;

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingTarget) {
      setSelectedAccountId(existingAccountWithTarget?.accountId ?? '');
      setTargetType(existingTarget.targetType);
      setTargetValue(existingTarget.targetValue ?? '');
      setTargetValueMax(existingTarget.targetValueMax ?? '');
    } else {
      // Reset form for new target
      setSelectedAccountId('');
      setTargetType('exact_amount');
      setTargetValue('');
      setTargetValueMax('');
    }
  }, [isEditing, existingTarget, existingAccountWithTarget, accountId]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!targetValue) return;

    if (isEditing && accountId) {
      const data: UpdateAccountTargetRequest = {
        targetType,
        targetValue,
        targetValueMax: targetType === 'range' ? targetValueMax || undefined : undefined,
      };
      updateTarget(
        { accountId, data },
        { onSuccess: () => handleClose() }
      );
    } else {
      if (!selectedAccountId) return;
      const data: CreateAccountTargetRequest = {
        accountId: selectedAccountId,
        targetType,
        targetValue,
        targetValueMax: targetType === 'range' ? targetValueMax || undefined : undefined,
      };
      createTarget(data, { onSuccess: () => handleClose() });
    }
  };

  const handleClose = () => {
    setSelectedAccountId('');
    setTargetType('exact_amount');
    setTargetValue('');
    setTargetValueMax('');
    onClose();
  };

  const isPending = isCreating || isUpdating;
  const canSubmit = targetValue && (isEditing || selectedAccountId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Account Target' : 'Add Account Target'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Account selection (only for new targets) */}
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select an account...</option>
                  {availableAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type})
                    </option>
                  ))}
                </select>
                {availableAccounts.length === 0 && (
                  <p className="mt-1 text-xs text-yellow-600">
                    All accounts already have targets set
                  </p>
                )}
              </div>
            )}

            {/* Target type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Type
              </label>
              <select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as TargetType)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {Object.entries(TARGET_TYPE_DISPLAY_NAMES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Target values based on type */}
            {targetType === 'exact_amount' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {targetType === 'range' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      value={targetValue}
                      onChange={(e) => setTargetValue(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      value={targetValueMax}
                      onChange={(e) => setTargetValueMax(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {targetType === 'months_expenses' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Months of Expenses
                </label>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="6"
                  min="1"
                  max="24"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Target will be calculated based on your average monthly expenses
                </p>
              </div>
            )}

            {targetType === 'percentage_income' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Percentage of Monthly Income
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    placeholder="10"
                    min="1"
                    max="100"
                    className="w-full pr-8 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={isPending}
            >
              {isEditing ? 'Update Target' : 'Create Target'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
